// Beziehungen zwischen Teilen — engine-frei.
// „Heften“: part.meta.attached_to = client_id des Ankers; bewegt sich der Anker,
// wandern alle (transitiv) gehefteten Teile mit. Einbahnstraße, keine Zyklen.
// „Ausrichten“: einmalige Bündig-/Zentrier-Operation auf Welt-AABB-Basis.
import { partAabb } from "./snapping"

export function anchorOf(part) {
  return part.meta?.attached_to ?? null
}

export function withAnchor(part, anchorId) {
  const meta = { ...part.meta }
  if (anchorId) meta.attached_to = anchorId
  else delete meta.attached_to
  return meta
}

// Alle transitiv an rootId gehefteten Teile (ohne rootId selbst)
export function descendantIdsOf(doc, rootId) {
  const result = []
  const seen = new Set([rootId])
  const visit = (id) => {
    for (const part of doc.allParts()) {
      if (anchorOf(part) === id && !seen.has(part.client_id)) {
        seen.add(part.client_id)
        result.push(part.client_id)
        visit(part.client_id)
      }
    }
  }
  visit(rootId)
  return result
}

export function wouldCreateCycle(doc, childId, anchorId) {
  let current = anchorId
  const seen = new Set()
  while (current) {
    if (current === childId || seen.has(current)) return true
    seen.add(current)
    current = anchorOf(doc.getPart(current) ?? {})
  }
  return false
}

// Verteilen: count Exemplare des Teils gleichmäßig in die Lücke zwischen den
// Innenflächen zweier Grenzteile legen. Achse = größter Zentrenabstand der
// Grenzteile. Liefert Achsen-Feld + Mittelpunkte oder null, wenn es nicht passt.
export function distributePositions(part, boundaryA, boundaryB, count) {
  if (count < 1) return null
  const a = partAabb(boundaryA)
  const b = partAabb(boundaryB)

  let axis = 0
  let bestDistance = -1
  for (const i of [ 0, 1, 2 ]) {
    const distance = Math.abs((a.min[i] + a.max[i]) - (b.min[i] + b.max[i])) / 2
    if (distance > bestDistance) { bestDistance = distance; axis = i }
  }

  const [lower, upper] = (a.min[axis] + a.max[axis]) <= (b.min[axis] + b.max[axis]) ? [ a, b ] : [ b, a ]
  const start = lower.max[axis]
  const end = upper.min[axis]

  const own = partAabb(part)
  const extent = own.max[axis] - own.min[axis]
  const free = end - start - count * extent
  if (free < 0) return null

  const gap = free / (count + 1)
  const positions = Array.from({ length: count }, (_, i) =>
    Math.round(start + gap * (i + 1) + extent * i + extent / 2)
  )
  return { field: [ "pos_x", "pos_y", "pos_z" ][axis], positions }
}

// Kantenbündig: bewegt ein Teil so, dass seine nächstliegende Seite in der
// Ebene der gewählten Kante des Referenzteils liegt (l1/l2 = vorn/hinten → z,
// b1/b2 = links/rechts → x).
export function alignToEdgeDelta(movingPart, referencePart, edgeKey) {
  const axis = edgeKey.startsWith("l") ? 2 : 0
  const ref = partAabb(referencePart)
  const plane = edgeKey === "l1" || edgeKey === "b1" ? ref.min[axis] : ref.max[axis]

  const moving = partAabb(movingPart)
  const deltaToMin = plane - moving.min[axis]
  const deltaToMax = plane - moving.max[axis]
  const delta = Math.abs(deltaToMin) <= Math.abs(deltaToMax) ? deltaToMin : deltaToMax
  return { field: axis === 0 ? "pos_x" : "pos_z", delta: Math.round(delta) }
}

// Modi: <achse>_<kante>: x_min = links bündig, x_max = rechts, x_center = mittig;
// z: vorn/hinten, y: unten/oben. Liefert Achsen-Feld + ganzzahliges Delta.
export function alignDelta(movingPart, referencePart, mode) {
  const a = partAabb(movingPart)
  const b = partAabb(referencePart)
  const [axisKey, kind] = mode.split("_")
  const axis = { x: 0, y: 1, z: 2 }[axisKey]
  let delta
  if (kind === "min") delta = b.min[axis] - a.min[axis]
  else if (kind === "max") delta = b.max[axis] - a.max[axis]
  else delta = (b.min[axis] + b.max[axis]) / 2 - (a.min[axis] + a.max[axis]) / 2
  return { field: [ "pos_x", "pos_y", "pos_z" ][axis], delta: Math.round(delta) }
}
